/**
 * useSourceDataLoader Hook
 *
 * React hook for loading all source data types required for DataReferenceTreeWrapper.
 * Wraps SourceDataLoaderService with React state management and lifecycle.
 *
 * Features:
 * - Auto-loading on mount (configurable)
 * - Loading state management
 * - Error handling
 * - Manual reload capability
 * - Memoized service instances
 *
 * Related: Sprint 11, Task T044
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import { audienceConverter } from '@modules/firebase-core/converters/audienceConverter';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { studyCaseConverter } from '@modules/firebase-core/converters/studyCaseConverter';
import {
  SourceDataLoaderService,
  type SourceDataLoadResult,
  type LoadOptions,
} from '../services/SourceDataLoaderService';

/**
 * Hook options
 */
export interface UseSourceDataLoaderOptions {
  /** Project ID to load data for */
  projectId: string;

  /** Auto-load data on mount (default: true) */
  autoLoad?: boolean;

  /** Maximum items per collection (default: 50) */
  limit?: number;
}

/**
 * Hook result
 */
export interface UseSourceDataLoaderResult {
  /** Loaded source data */
  data: SourceDataLoadResult;

  /** Loading state */
  loading: boolean;

  /** Error if loading failed */
  error: Error | null;

  /** Manually reload data */
  reload: () => Promise<void>;
}

/**
 * Empty data state
 */
const EMPTY_DATA: SourceDataLoadResult = {
  audiences: [],
  actorsByAudience: {},
  tasks: [],
  studyCases: [],
  reviews: [],
  ratings: [],
  bigNumbers: [],
};

/**
 * useSourceDataLoader Hook
 *
 * Loads all source data for a project with React state management.
 *
 * Usage:
 * ```typescript
 * // Auto-load on mount
 * const { data, loading, error } = useSourceDataLoader({ projectId });
 *
 * // Manual loading only
 * const { data, loading, reload } = useSourceDataLoader({
 *   projectId,
 *   autoLoad: false
 * });
 *
 * // Later...
 * await reload();
 *
 * // Access data
 * <DataReferenceTreeWrapper
 *   audiences={data.audiences}
 *   tasks={data.tasks}
 *   {...data}
 * />
 * ```
 */
export function useSourceDataLoader(
  options: UseSourceDataLoaderOptions
): UseSourceDataLoaderResult {
  const { projectId, autoLoad = true, limit = 50 } = options;

  // State
  const [data, setData] = useState<SourceDataLoadResult>(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Memoized service instances
  const audienceService = useMemo(() => new AudienceService(projectId, audienceConverter), [projectId]);
  const taskService = useMemo(() => new TaskService(projectId, taskConverter), [projectId]);
  const studyCaseService = useMemo(() => new StudyCaseService(projectId, studyCaseConverter), [projectId]);
  const reviewService = useMemo(() => new ReviewService(projectId), [projectId]);
  const ratingService = useMemo(() => new RatingService(projectId), [projectId]);
  const bigNumberService = useMemo(() => new BigNumberService(projectId), [projectId]);

  // Memoized loader service
  const loaderService = useMemo(
    () =>
      new SourceDataLoaderService(
        projectId,
        audienceService,
        taskService,
        studyCaseService,
        reviewService,
        ratingService,
        bigNumberService
      ),
    [projectId, audienceService, taskService, studyCaseService, reviewService, ratingService, bigNumberService]
  );

  // Load data function
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const loadOptions: LoadOptions = { limit };
      const result = await loaderService.loadAll(loadOptions);
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('[useSourceDataLoader] Load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [loaderService, limit]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  return {
    data,
    loading,
    error,
    reload: load,
  };
}
